import React from "react";
import { Button, Callout, Card, Dialog, Field, Input, PageHeader } from "../components/DesignSystem";
import type { SessionSummary } from "../domain/types";
import { validateChangePassword } from "../domain/validation";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { useSubmitGuard } from "./common";

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function formatSessionMeta(session: SessionSummary): string {
  const created = new Date(session.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return [session.userAgent, session.ipAddress, `since ${created}`].filter(Boolean).join(" · ");
}

function SessionsCard() {
  const { service } = usePlannerData();
  const [sessions, setSessions] = React.useState<SessionSummary[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busyToken, setBusyToken] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setSessions(await service.auth.listSessions());
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function revoke(token: string) {
    setBusyToken(token);
    try {
      await service.auth.revokeSession(token);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyToken(null);
    }
  }

  return (
    <Card eyebrow="Sessions" title="Signed-in devices">
      {error && <Callout tone="error" title="Couldn't load sessions">{error}</Callout>}
      {!sessions && !error && <p style={{ font: "var(--type-body)", color: "var(--text-secondary)" }}>Loading...</p>}
      {sessions && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {sessions.map((s) => (
            <div key={s.token} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-3)" }}>
              <div>
                <div style={{ font: "var(--type-label)" }}>{s.isCurrent ? "This device" : "Other device"}</div>
                <div style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{formatSessionMeta(s)}</div>
              </div>
              {!s.isCurrent && (
                <Button size="sm" variant="ghost" disabled={busyToken === s.token} onClick={() => revoke(s.token)}>
                  {busyToken === s.token ? "Signing out..." : "Sign out"}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DeleteWeddingDialog({ weddingTitle, onClose, onDeleted }: { weddingTitle: string; onClose: () => void; onDeleted: () => void }) {
  const { service } = usePlannerData();
  const [confirmText, setConfirmText] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setError(null);
    try {
      await service.weddings.deleteWedding();
      onDeleted();
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Delete this wedding"
      description="This permanently deletes every guest, event, budget, vendor, and document under this wedding. This cannot be undone."
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={confirmText !== weddingTitle || submitting} onClick={submit}>{submitting ? "Deleting..." : "Delete permanently"}</Button>
      </>}
    >
      {error && <Callout tone="error" title="Couldn't delete this wedding">{error}</Callout>}
      <Field label={`Type "${weddingTitle}" to confirm`}>
        <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
      </Field>
    </Dialog>
  );
}

function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const { service, signOut } = usePlannerData();
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setError(null);
    try {
      await service.auth.deleteAccount(password);
      await signOut();
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Delete your account"
      description="This permanently deletes any wedding you solely own, removes you from any wedding you collaborate on, and deletes your account. This cannot be undone."
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={!password || submitting} onClick={submit}>{submitting ? "Deleting..." : "Delete my account"}</Button>
      </>}
    >
      {error && <Callout tone="error" title="Couldn't delete your account">{error}</Callout>}
      <Field label="Confirm your current password">
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
    </Dialog>
  );
}

export function SecurityScreen() {
  const { session, workspace, signOut, service } = usePlannerData();
  const [draft, setDraft] = React.useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);
  const [deletingWedding, setDeletingWedding] = React.useState(false);
  const [deletingAccount, setDeletingAccount] = React.useState(false);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const validation = validateChangePassword(draft);

  const submit = () => guardedSubmit(async () => {
    setError(null);
    setSuccess(false);
    try {
      await service.auth.changePassword({ currentPassword: draft.currentPassword, newPassword: draft.newPassword });
      setDraft({ currentPassword: "", newPassword: "", confirm: "" });
      setSuccess(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  async function exportAccount() {
    setExportError(null);
    try {
      const data = await service.auth.exportAccountData();
      downloadJson(data, "pockets-of-beige-account-export.json");
    } catch (err) {
      setExportError(errorMessage(err));
    }
  }

  async function exportWedding() {
    setExportError(null);
    try {
      const data = await service.weddings.exportData();
      downloadJson(data, "pockets-of-beige-wedding-export.json");
    } catch (err) {
      setExportError(errorMessage(err));
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Account"
        title="Security"
        meta={[{ icon: "shield-check", label: "Password and session" }, { icon: "mail", label: session?.user.email || "" }]}
      />
      <div className="pob-pad" style={{ maxWidth: 520, display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
        <Card eyebrow="Password" title="Change password">
          {error && <Callout tone="error" title="Password not changed">{error}</Callout>}
          {success && <Callout tone="success" title="Password changed">Your password was updated. Use it next time you sign in.</Callout>}
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <Field label="Current password" error={validation.errors.currentPassword}>
              <Input type="password" value={draft.currentPassword} onChange={(event) => setDraft({ ...draft, currentPassword: event.target.value })} invalid={Boolean(validation.errors.currentPassword)} />
            </Field>
            <Field label="New password" error={validation.errors.newPassword}>
              <Input type="password" value={draft.newPassword} onChange={(event) => setDraft({ ...draft, newPassword: event.target.value })} invalid={Boolean(validation.errors.newPassword)} />
            </Field>
            <Field label="Confirm new password" error={validation.errors.confirm}>
              <Input type="password" value={draft.confirm} onChange={(event) => setDraft({ ...draft, confirm: event.target.value })} invalid={Boolean(validation.errors.confirm)} />
            </Field>
            <div>
              <Button variant="primary" disabled={!validation.valid || submitting} onClick={submit}>{submitting ? "Saving…" : "Change password"}</Button>
            </div>
          </div>
        </Card>
        <Card eyebrow="Locked out?" title="Forgot your password">
          <p style={{ font: "var(--type-body)", color: "var(--text-secondary)" }}>
            While you're signed in, use Change password above. If you're ever signed out and locked out, use "Forgot password" on the sign-in screen -- we'll email you a reset code.
          </p>
        </Card>
        <SessionsCard />
        <Card eyebrow="Session" title="This device">
          <p style={{ font: "var(--type-body)", color: "var(--text-secondary)", marginBottom: "var(--space-4)" }}>
            Signed in as {session?.user.name} ({session?.user.email}). Signing out ends your session on this device.
          </p>
          <Button variant="danger" icon="log-out" onClick={() => { void signOut(); }}>Sign out</Button>
        </Card>
        <Card eyebrow="Your data" title="Export">
          {exportError && <Callout tone="error" title="Export failed">{exportError}</Callout>}
          <p style={{ font: "var(--type-body)", color: "var(--text-secondary)", marginBottom: "var(--space-4)" }}>
            Download a copy of your account details, or everything under this wedding, as a JSON file.
          </p>
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <Button variant="secondary" icon="download" onClick={exportAccount}>Export account data</Button>
            <Button variant="secondary" icon="download" onClick={exportWedding}>Export wedding data</Button>
          </div>
        </Card>
        <Card eyebrow="Danger zone" title="Delete">
          <p style={{ font: "var(--type-body)", color: "var(--text-secondary)", marginBottom: "var(--space-4)" }}>
            These actions are permanent and cannot be undone.
          </p>
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <Button variant="danger" onClick={() => setDeletingWedding(true)}>Delete this wedding</Button>
            <Button variant="danger" onClick={() => setDeletingAccount(true)}>Delete my account</Button>
          </div>
        </Card>
      </div>
      {deletingWedding && (
        <DeleteWeddingDialog
          weddingTitle={workspace?.wedding.title || ""}
          onClose={() => setDeletingWedding(false)}
          onDeleted={() => { void signOut(); }}
        />
      )}
      {deletingAccount && <DeleteAccountDialog onClose={() => setDeletingAccount(false)} />}
    </div>
  );
}
